/**
 * Encompass Asset Account Type helpers
 * -----------------------------------
 * - Group logic unchanged (still grouped rows + children)
 * - Allowed % is now driven by CX.LOAN.PRODUCT.NAME (selected product)
 * - Retirement is still age-based BUT rules vary by select for SUPER JUMBO
 * - Only supported account types appear in the grid
 *
 * `selectedProduct` throughout this file is a canonical loan-program code
 * (CX.LOAN.PRODUCT.NAME as translated by src/utils/loanProgramUtil.js), not a
 * brand name.
 */

import { LOAN_PROGRAM_CODES } from "../config/organizations";

export const ACCOUNT_TYPE_LABEL = {
    CheckingAccount: "Checking Account",
    SavingsAccount: "Savings Account",
    MoneyMarketFund: "Money Market Fund",

    // Encompass VOD option is "MutualFund" (singular)
    MutualFund: "Mutual Funds",
    Stock: "Stock",
    Bond: "Bond",

    // Retirement option is "RetirementFund"
    RetirementFund: "Retirement Funds",
    RetirementFunds: "Retirement Funds"
};

// Valid supported types only
export const SUPPORTED_ACCOUNT_TYPES = [
    "CheckingAccount",
    "SavingsAccount",
    "MoneyMarketFund",
    "MutualFund",
    "Stock",
    "Bond",
    "RetirementFund",
    "RetirementFunds"
];

/**
 * IMPORTANT: group names are fixed as per valid group list.
 * We do NOT add other types (Stock Options, CD, etc.) to groups.
 */
export const ACCOUNT_TYPE_GROUPS = [
    {
        groupName: "Checking, Savings, & Money Market Accounts",
        types: ["CheckingAccount", "SavingsAccount", "MoneyMarketFund"],
        allowedPercent: 1.0
    },
    {
        groupName: "Stocks & Bonds Accounts",
        types: ["MutualFund", "Stock", "Bond"],
        allowedPercent: 0.8 // default (will be overridden dynamically)
    },
    {
        groupName: "Retirement Funds",
        types: ["RetirementFund", "RetirementFunds"],
        allowedPercent: null // dynamic by age and selected Product
    }
];

export const FIELD_IDS = {
    requiredFundsToClose: "1546",
    totalVerifiedAssets: "1547",

    // Program code used for eligibility lookup
    loanProgram: "CX.LOAN.PROGRAM.IDENTIFIER",

    incomeType: "CX.INCOME.TYPE",
    qualifyingPitiaPayment: "912",
    monthsReservesRequired: "1548",
    assetDeplVodJson: "CX.ASSET.DEPL.VOD.JSON",
    assetUtilIncomeTotal: "CX.NONQ.46",
    docType: "CX.DOC.TYPE",
    loanAmount: "1109",
    loanProductName: "CX.LOAN.PRODUCT.NAME",
    additionalIncomeDescription: "1818",
    incomeBorrOtherIncome: "1815",

    loanProgram_1401: "1401",
    shortTermRental: "CX.Short.Term.Rental",
    dscrCalculated: "CX.DSCR.CALCULATED",
    nonq22: "CX.NONQ.22",
    nonq23: "CX.NONQ.23",
    nonq24: "CX.NONQ.24",
    nonq25: "CX.NONQ.25",
    nonq26: "CX.NONQ.26",
    nonq27: "CX.NONQ.27",
    nonq28: "CX.NONQ.28",
    nonq29: "CX.NONQ.29",
    nonq47: "CX.NONQ.47",
    nonq48: "CX.NONQ.48",

    verifiedAsset: "CX.VERIFIED.ASSET",
    verifiedAssetReserve: "CX.VERIFIED.ASSET.RESERVE",
    cashFromToBorrower: "142",

    verifiedAssetDownPayment: "CX.VERIFIED.ASSET.DOWNPAYMENT",
    reservesActual: "CX.RESERVES.ACTUAL",
    reservesNoCashout: "CX.RESERVES.NOCASHOUT",
    ltv_353: "353",
    pitia_912: "912",
    cashInHand_142: "142",
};

/**
 * Loan Program rules (dynamic config)
 * - programCode: CX.LOAN.PROGRAM.IDENTIFIER
 * - allowed: true/false
 * - calcType: "DTI" | "DSCR" | "N/A"
 * - months: number or null
 * - strDisallow: if true, and STR (CX.Short.Term.Rental) field is "Yes" => not allowed
 * - allowedIncomeTypes: optional list of CX.INCOME.TYPE values. When present,
 *   the loan is only eligible if CX.INCOME.TYPE is one of them. Omit the key
 *   to keep the program open to every income type (existing behaviour).
 */
const ASSET_DEPL_LOAN_PROGRAM_CONFIG = [
    { programCode: "BPACC", allowed: true, calcType: "DSCR", months: 60, strDisallow: true }, // BPACC allowed unless STR=Yes
    { programCode: "BPACC58", allowed: false, calcType: "N/A", months: null, strDisallow: false },
    { programCode: "BPACCINC", allowed: true, calcType: "DTI", months: 60, strDisallow: false },
    { programCode: "BITIN", allowed: true, calcType: "DSCR", months: 60, strDisallow: false },
    { programCode: "CACT", allowed: true, calcType: "DTI", months: 60, strDisallow: false },
    {
        programCode: "CACTTX6",
        allowed: true,
        calcType: "DTI",
        months: 60,
        strDisallow: false,
        // CACTTX6 is bank-statement only
        allowedIncomeTypes: [
            "Bank Statement - Standard 12 months",
            "Bank Statement - Standard 24 months"
        ]
    },
    { programCode: "CITIN", allowed: true, calcType: "DTI", months: 60, strDisallow: false },
    { programCode: "CALLY", allowed: false, calcType: "N/A", months: null, strDisallow: false },
    { programCode: "CFNINC", allowed: true, calcType: "DTI", months: 60, strDisallow: false },
    { programCode: "BAMFN", allowed: true, calcType: "DSCR", months: 60, strDisallow: false },
    { programCode: "NQMJUMBO", allowed: true, calcType: "DTI", months: 84, strDisallow: false },
    { programCode: "SJBPACC", allowed: true, calcType: "DSCR", months: 60, strDisallow: false }
];

/*
 * Allowed AU % by Selected Loan Product (CX.LOAN.PRODUCT.NAME), keyed by
 * canonical loan-program code so one table serves every company.
 *
 * IMPORTANT: ONLY these percentages are used. No fallback - a product absent
 * from this table yields 0% usable, which is the existing behaviour for the
 * EQUITY FIRST products (PROGRAM_010 / PROGRAM_011) and ACTIVATOR PRIME
 * (PROGRAM_009), none of which were listed here before this change either.
 *
 * TODO: PROGRAM_012 (SKY PLUS, SkyTPO only) has no rules yet and is
 * deliberately absent until they are supplied.
 */
export const AU_PERCENT_CONFIG_BY_PRODUCT_SELECT = {
    // First set
    // ACCELERATOR / BREEZY
    [LOAN_PROGRAM_CODES.PROGRAM_002]: {
        CheckingAccount: 1.0,
        SavingsAccount: 1.0,
        MoneyMarketFund: 1.0,
        MutualFund: 1.0,
        MutualFunds: 1.0, // support plural token
        Stock: 1.0,
        Bond: 1.0,
        RetirementFund: { under: 0.7, over: 1.0 },
        RetirementFunds: { under: 0.7, over: 1.0 }
    },
    // ACCELERATOR INCOME / BREEZY INCOME
    [LOAN_PROGRAM_CODES.PROGRAM_003]: {
        CheckingAccount: 1.0,
        SavingsAccount: 1.0,
        MoneyMarketFund: 1.0,
        MutualFund: 1.0,
        MutualFunds: 1.0,
        Stock: 1.0,
        Bond: 1.0,
        RetirementFund: { under: 0.7, over: 1.0 },
        RetirementFunds: { under: 0.7, over: 1.0 }
    },
    // AMBASSADOR / PASSPORT
    [LOAN_PROGRAM_CODES.PROGRAM_007]: {
        CheckingAccount: 1.0,
        SavingsAccount: 1.0,
        MoneyMarketFund: 1.0,
        MutualFund: 1.0,
        MutualFunds: 1.0,
        Stock: 1.0,
        Bond: 1.0,
        RetirementFund: { under: 0.7, over: 1.0 },
        RetirementFunds: { under: 0.7, over: 1.0 }
    },
    // ACCELERATOR ITIN / BREEZY ITIN
    [LOAN_PROGRAM_CODES.PROGRAM_005]: {
        CheckingAccount: 1.0,
        SavingsAccount: 1.0,
        MoneyMarketFund: 1.0,
        MutualFund: 1.0,
        MutualFunds: 1.0,
        Stock: 1.0,
        Bond: 1.0,
        RetirementFund: { under: 0.7, over: 1.0 },
        RetirementFunds: { under: 0.7, over: 1.0 }
    },
    // ACTIVATOR / SUNNY
    [LOAN_PROGRAM_CODES.PROGRAM_004]: {
        CheckingAccount: 1.0,
        SavingsAccount: 1.0,
        MoneyMarketFund: 1.0,
        MutualFund: 1.0,
        MutualFunds: 1.0,
        Stock: 1.0,
        Bond: 1.0,
        RetirementFund: { under: 0.7, over: 1.0 },
        RetirementFunds: { under: 0.7, over: 1.0 }
    },
    // ACTIVATOR ITIN / SUNNY ITIN
    [LOAN_PROGRAM_CODES.PROGRAM_006]: {
        CheckingAccount: 1.0,
        SavingsAccount: 1.0,
        MoneyMarketFund: 1.0,
        MutualFund: 1.0,
        MutualFunds: 1.0,
        Stock: 1.0,
        Bond: 1.0,
        RetirementFund: { under: 0.7, over: 1.0 },
        RetirementFunds: { under: 0.7, over: 1.0 }
    },

    // Second set
    // SUPER JUMBO / STARRY
    [LOAN_PROGRAM_CODES.PROGRAM_008]: {
        CheckingAccount: 1.0,
        SavingsAccount: 1.0,
        MoneyMarketFund: 1.0,
        MutualFund: 0.8,
        MutualFunds: 0.8,
        Stock: 0.8,
        Bond: 0.8,
        RetirementFund: { under: 0.6, over: 0.7 },
        RetirementFunds: { under: 0.6, over: 0.7 }
    },

    // Third set
    // ALLY (ChampsTPO only)
    [LOAN_PROGRAM_CODES.PROGRAM_001]: {
        CheckingAccount: 1.0,
        SavingsAccount: 1.0,
        MoneyMarketFund: 1.0,
        MutualFund: 1.0,
        MutualFunds: 1.0,
        Stock: 1.0,
        Bond: 1.0,
        RetirementFund: { under: 0.7, over: 1.0 },
        RetirementFunds: { under: 0.7, over: 1.0 }
    }
};


// CLI loans => not allowed
const DISALLOWED_PROGRAM_PREFIXES = ["CLI"];

export function normalizeYesNo(val) {
    const s = String(val ?? "").trim().toUpperCase();
    if (!s) return "";
    if (s === "Y" || s === "YES" || s === "TRUE" || s === "1") return "YES";
    if (s === "N" || s === "NO" || s === "FALSE" || s === "0") return "NO";
    return s;
}

export function getProgramConfig(programCodeRaw) {
    const programCode = String(programCodeRaw ?? "").trim().toUpperCase();
    if (!programCode) return null;
    return ASSET_DEPL_LOAN_PROGRAM_CONFIG.find(x => String(x.programCode).toUpperCase() === programCode) || null;
}

// Compare income types loosely: case/space insensitive, and tolerant of the
// en/em dashes Encompass sometimes stores instead of a plain hyphen.
function normalizeIncomeType(val) {
    return String(val ?? "")
        .replace(/[‐-―]/g, "-")
        .replace(/\s+/g, " ")
        .trim()
        .toUpperCase();
}

/**
 * Income-type gate for a program config row.
 * - config without `allowedIncomeTypes` => allowed (unchanged behaviour)
 * - config with `allowedIncomeTypes` => CX.INCOME.TYPE must match one of them
 *   (an empty/missing income type is NOT a match)
 */
export function isIncomeTypeAllowed(cfg, incomeTypeRaw) {
    const allowedList = cfg?.allowedIncomeTypes;
    if (!Array.isArray(allowedList) || allowedList.length === 0) return true;

    const incomeType = normalizeIncomeType(incomeTypeRaw);
    if (!incomeType) return false;

    return allowedList.some(x => normalizeIncomeType(x) === incomeType);
}

export function isCliProgram(programCodeRaw) {
    const programCode = String(programCodeRaw ?? "").trim().toUpperCase();
    if (!programCode) return false;
    return DISALLOWED_PROGRAM_PREFIXES.some(p => programCode.startsWith(p));
}

export function toNumber(val) {
    if (val === null || val === undefined) return 0;
    const n = Number(String(val).replace(/,/g, ""));
    return Number.isFinite(n) ? n : 0;
}

export function money(val) {
    const n = toNumber(val);
    return n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// birthDate: "YYYY-MM-DD" (or ISO). Returns decimal years (e.g. 59.6)
export function calcAgeYearsFromBirthDate(birthDate, asOfDate = new Date()) {
    if (!birthDate) return 0;

    const s = String(birthDate).trim();
    if (!s) return 0;

    // Use local midnight to avoid timezone day-shift issues
    const parts = s.split("T")[0].split("-");
    if (parts.length < 3) return 0;

    const y = Number(parts[0]);
    const m = Number(parts[1]);
    const d = Number(parts[2]);

    if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return 0;

    const dob = new Date(y, m - 1, d);
    if (Number.isNaN(dob.getTime())) return 0;

    const msPerDay = 24 * 60 * 60 * 1000;
    const days = (asOfDate - dob) / msPerDay;

    // 365.2425 average year length
    const years = days / 365.2425;
    return Number.isFinite(years) ? years : 0;
}

// Retirement bucket uses decimal age
export function isOver59Half(ageYears) {
    const a = Number(ageYears);
    const age = Number.isFinite(a) ? a : 0;
    return age >= 59.5;
}


export function getRetirementLabelByAgeYears(ageYears) {
    return isOver59Half(ageYears)
        ? "401k & Retirement Accounts (over 59 ½ years old)"
        : "401k & Retirement Accounts (under 59 ½ years old)";
}


export function getAccountTypeLabel(typeToken, ageYears) {
    const t = String(typeToken || "");

    //Child row label: show simple type name
    if (t === "RetirementFund" || t === "RetirementFunds") {
        // If you want exact text:
        return "Retirement Fund"; // or "Retirement Funds"
        // OR if you prefer your mapping:
        // return ACCOUNT_TYPE_LABEL[t] || "Retirement Fund";
    }

    return ACCOUNT_TYPE_LABEL[t] || t || "";
}


/**
 * Group name:
 * - Checking/Savings/MM => "Checking, Savings, & Money Market Accounts"
 * - MutualFund/Stock/Bond => "Stocks & Bonds Accounts"
 * - Retirement => age split label
 */
export function getAccountTypeGroupName(typeToken, ageYears) {
    const t = String(typeToken || "");

    if (t === "RetirementFund" || t === "RetirementFunds") {
        return getRetirementLabelByAgeYears(ageYears);
    }

    // For other supported, map to fixed groups
    const grp = ACCOUNT_TYPE_GROUPS.find(g => g.types.includes(t));
    return grp?.groupName || "";
}

export function getAllowedPercent(typeToken, ageYears, selectedProduct) {

    const t = String(typeToken || "");
    const sel = String(selectedProduct || "").trim().toUpperCase();

    const cfg = AU_PERCENT_CONFIG_BY_PRODUCT_SELECT[sel];
    if (!cfg) return 0; // if selectproduct not recognized, 0% usable

    const rule = cfg[t];
    if (rule === undefined || rule === null) return 0; // type not defined => 0%

    // Retirement uses {under, over}
    if (typeof rule === "object") {
        return isOver59Half(ageYears) ? Number(rule.over) : Number(rule.under);
    }

    // Other types are number
    const pct = Number(rule);
    return Number.isFinite(pct) ? pct : 0;
}


/**
 * Build grouped tree rows for AntD Table
 * Adds groupInfo:
 * - if allowedPercent < 1 => show "Max usable 80% of cash value"
 * - else => "No limit (can use up to cash value)"
 */
export function buildGroupedVodRows(rows, toNumberFn) {
    const map = new Map();

    (rows || []).forEach((r) => {
        const groupName = r.groupName;
        if (!groupName) return;
        if (!map.has(groupName)) map.set(groupName, []);
        map.get(groupName).push(r);
    });

    const buildGroupInfo = (children) => {
        const pct = Number(children?.[0]?.allowedPercent ?? 1);

        if (Number.isFinite(pct) && pct < 1) {
            return `Max usable ${Math.round(pct * 100)}% of cash value`;
        }
        return "No limit (can use up to cash value)";
    };

    return Array.from(map.entries()).map(([groupName, children]) => {
        const cashValue = children.reduce((s, x) => s + toNumberFn(x.cashValue), 0);
        const amountUsed = children.reduce((s, x) => s + toNumberFn(x.amountUsed), 0);

        const enhancedChildren = children.map((c, idx) => ({
            ...c,
            isLastInGroup: idx === children.length - 1
        }));

        // add subtotal row as the LAST child row (inside sub-grid)
        enhancedChildren.push({
            key: `subtotal__${groupName}`.toLowerCase(),
            isGroupSubtotal: true,
            typeLabel: "Subtotal",
            holderName: "",
            depositoryAccountName: "",
            accountIdentifier: "",
            cashValue,
            amountUsed,
            useForAus: false
        });

        return {
            key: `group__${groupName}`,
            isGroup: true,
            typeLabel: groupName,
            groupInfo: buildGroupInfo(children),
            cashValue: "",
            amountUsed: "",
            children: enhancedChildren
        };
    });
}


/**
 * Build flat grid rows from VODs
 * - amountUsed seeded from API negative (not JSON)
 * - allowedPercent and allowedMax computed from selected product + age
 * - ONLY supported types are included
 *
 * NOTE: added selectedProduct param (required for dynamic allowed %)
 */
export function buildVODGridRows(vods, ownerAgeYearsMap, selectedProduct) {
    const map = new Map();

    (vods || []).forEach((vod) => {
        const vodId = vod?.id;
        const items = Array.isArray(vod?.items) ? vod.items : [];
        const holderName = vod?.holderName || "";

        const owner = vod?.owner || ""; // "Borrower" | "CoBorrower" | ""
        const ageYears =
            owner === "CoBorrower"
                ? (ownerAgeYearsMap?.CoBorrower ?? 0)
                : (ownerAgeYearsMap?.Borrower ?? 0);

        const emptyItemNumbers = items
            .filter(i => i?.itemNumber && !i?.type && !i?.accountIdentifier)
            .map(i => i.itemNumber)
            .sort((a, b) => a - b);

        items.forEach((item) => {
            const type = item?.type || "";
            const accountIdentifier = item?.accountIdentifier || "";
            const amt = toNumber(item?.urla2020CashOrMarketValueAmount);
            const depositoryAccountName = item?.depositoryAccountName || "";

            if (!type || !accountIdentifier) return;

            // only supported account types
            if (!SUPPORTED_ACCOUNT_TYPES.includes(String(type))) return;

            const key = `${vodId}__${type}__${accountIdentifier}`.toLowerCase();

            // FIX: use ageYears (not hmdaAge)
            const allowedPercent = getAllowedPercent(type, ageYears, selectedProduct);
            if (!Number.isFinite(Number(allowedPercent))) return;

            // FIX: use ageYears (not hmdaAge)
            const typeLabel = getAccountTypeLabel(type, ageYears);
            const groupName = getAccountTypeGroupName(type, ageYears);

            // groupName must exist (we only show types in those three buckets)
            if (!groupName) return;

            if (!map.has(key)) {
                map.set(key, {
                    key,
                    vodId,
                    type,
                    typeLabel,
                    groupName,
                    holderName,
                    accountIdentifier,
                    depositoryAccountName: depositoryAccountName || "",
                    cashValue: 0,
                    amountUsed: "",
                    useForAus: false,

                    ageYears,
                    owner,
                    allowedPercent,
                    allowedMax: 0,

                    positiveItemNumber: null,
                    negativeItemNumber: null,
                    emptyItemNumbers
                });
            }

            const row = map.get(key);

            if (!row.holderName && holderName) row.holderName = holderName;

            if (!row.depositoryAccountName && depositoryAccountName) {
                row.depositoryAccountName = depositoryAccountName;
            }

            // cash value comes from positive entries
            if (amt > 0) {
                row.cashValue += amt;
                if (!row.positiveItemNumber) row.positiveItemNumber = item?.itemNumber || null;
            }

            // amount used comes from negative entries
            if (amt < 0) {
                row.amountUsed = money(Math.abs(amt));
                row.negativeItemNumber = item?.itemNumber || null;
            }
        });
    });

    const rows = Array.from(map.values()).map(r => ({
        ...r,
        allowedPercent: getAllowedPercent(r.type, r.ageYears, selectedProduct),
        allowedMax: toNumber(r.cashValue) * (Number(getAllowedPercent(r.type, r.ageYears, selectedProduct)) || 0)
    }));

    // Only show if cashValue > 0
    return rows.filter(r => toNumber(r.cashValue) > 0);
}

